import type {
  CreateLessonClassData,
  CreateLessonClassRequest,
  DeleteLessonClassData,
  GetAcademyLessonClassListData,
  GetAcademyLessonClassListParams,
  GetAssignedTeacherListData,
  GetLessonClassDetailData,
  GetLessonClassMemberWithAttendanceData,
  GetLessonClassMemberWithAttendanceParams,
  GetLessonClassMemberWithPhoneData,
  OrderLessonClassData,
  Pageable,
  PatchLessonClassData,
  PatchLessonClassRequest,
} from '~/api-spec/__generated__/main'
import { httpClient, type RequestMeta } from '~/http/client'
import type { WithDefaultedOptionals, WithNullableOptionals } from '~/http/request'
import type { Slice } from '~/schema/slice'

import {
  adaptLessonClassAttendanceGroups,
  adaptLessonClassDetail,
  adaptLessonClasses,
  adaptLessonClassPhoneGroups,
  adaptLessonClassTeachers,
} from './adapters'
import type {
  LessonClass,
  LessonClassAttendanceGroup,
  LessonClassDetail,
  LessonClassId,
  LessonClassPhoneGroup,
  LessonClassTeacher,
} from './schemas'

// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 lessonClass/controller/LessonClassController.kt·request/ 로 확인.
// 생성 타입은 Spring Pageable 을 `pageable` 객체로 그리지만 실제 쿼리는 page·size·sort 를 평탄하게 받는다.

// 검색 조건은 서버가 nullable 로 받는다. 담임(HOMEROOM) 선생님이면 서버가 teacherId 를 자기 자신으로 바꾼다.
export type LessonClassListParams = WithNullableOptionals<
  Omit<GetAcademyLessonClassListParams, 'pageable'>
> &
  Pageable

export const getLessonClasses = async (
  params: LessonClassListParams,
  meta?: RequestMeta,
): Promise<Slice<LessonClass>> => {
  const body = await httpClient().get<GetAcademyLessonClassListData>('/lesson-classes', {
    params,
    meta,
  })
  return adaptLessonClasses(body.data)
}

export const getLessonClassDetail = async (
  lessonClassId: LessonClassId,
  meta?: RequestMeta,
): Promise<LessonClassDetail> => {
  const body = await httpClient().get<GetLessonClassDetailData>(
    `/lesson-classes/${lessonClassId}`,
    { meta },
  )
  return adaptLessonClassDetail(body.data)
}

/** 반을 하나라도 맡은 선생님. 선생님 정보를 찾지 못한 ID 는 서버가 뺀다 */
export const getLessonClassTeachers = async (meta?: RequestMeta): Promise<LessonClassTeacher[]> => {
  const body = await httpClient().get<GetAssignedTeacherListData>('/lesson-classes/teacher', {
    meta,
  })
  return adaptLessonClassTeachers(body.data) ?? []
}

// grade 는 Kotlin `Int`(기본값 없음)지만 Jackson 이 빠진 원시값을 0 으로 채워 받는다. 선생님 앱은 반 학년을
// 다루지 않아 보내지 않는다. 생성·수정 요청 클래스가 같은 모양이다.
export type LessonClassBody = WithDefaultedOptionals<CreateLessonClassRequest, 'grade'> &
  WithDefaultedOptionals<PatchLessonClassRequest, 'grade'>

export const createLessonClass = async (
  body: LessonClassBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post<CreateLessonClassData>('/lesson-classes', body, { meta })
}

export const updateLessonClass = async (
  lessonClassId: LessonClassId,
  body: LessonClassBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().patch<PatchLessonClassData>(`/lesson-classes/${lessonClassId}`, body, {
    meta,
  })
}

/** 반 순서를 이 ID 순서로 다시 매긴다 */
export const updateLessonClassOrder = async (
  lessonClassIds: LessonClassId[],
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().put<OrderLessonClassData>('/lesson-classes/order', lessonClassIds, { meta })
}

export const deleteLessonClasses = async (
  lessonClassIds: LessonClassId[],
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().delete<DeleteLessonClassData>('/lesson-classes', {
    data: lessonClassIds,
    meta,
  })
}

// studentStatus 를 빼면 서버는 재원(ACTIVE) 학생만 싣는다. bypassRoleScope 를 켜면 담임 선생님도 학원 전체 반을 받는다.
// 학생 ID 로 거르면 그 학생이 하나도 없는 반은 빠진다.
export type LessonClassAttendanceGroupParams = Omit<
  GetLessonClassMemberWithAttendanceParams,
  'pageable'
> &
  Pageable

/** 반별로 묶은 학생과 오늘 출결 */
export const getLessonClassAttendanceGroups = async (
  params: LessonClassAttendanceGroupParams,
  meta?: RequestMeta,
): Promise<Slice<LessonClassAttendanceGroup>> => {
  const body = await httpClient().get<GetLessonClassMemberWithAttendanceData>(
    '/lesson-classes/attendance',
    { params, meta },
  )
  return adaptLessonClassAttendanceGroups(body.data)
}

/** 반별로 묶은 재원 학생과 학생·보호자 전화번호 */
export const getLessonClassPhoneGroups = async (
  params?: Pageable,
  meta?: RequestMeta,
): Promise<Slice<LessonClassPhoneGroup>> => {
  const body = await httpClient().get<GetLessonClassMemberWithPhoneData>('/lesson-classes/phone', {
    params,
    meta,
  })
  return adaptLessonClassPhoneGroups(body.data)
}
